import { VIEW_MODE } from '../../../common/review-side-panel/settings/types';
import { COMMON_FEATURE_FLAGS } from '../../../modernization-common/constants/feature-flags';
export const ANNOTATION_TYPE = ({
  RECTANGLE: 'RECTANGLE',
  CUBOID: 'CUBOID',
  ELLIPSE: 'ELLIPSE',
  POLYGON: 'POLYGON',
  POLYLINE: 'POLYLINE',
  KEY_POINT: 'KEY_POINT',
  SEGMENTATION: 'SEGMENTATION',
  SKELETON: 'SKELETON',
  FRAME_CLASSIFICATION: 'FRAME_CLASSIFICATION'
} as const);
export type AnnotationType = (typeof ANNOTATION_TYPE)[keyof typeof ANNOTATION_TYPE];
export const LABEL_VALIDATIONS = Object.freeze({
  REQUIRED_LABELS_NOT_AVAILABLE: 'REQUIRED_LABELS_NOT_AVAILABLE',
  NO_LABEL_AVAILABLE: 'NO_LABEL_AVAILABLE'
});
export const SPEED_OPTIONS = [2, 1.75, 1.5, 1.25, 1, 0.75, 0.5, 0.25, 0.1];
export const STAGE_EVENTS = ({
  POINTER_UP: 'pointerup',
  POINTER_DOWN: 'pointerdown',
  POINTER_MOVE: 'pointermove',
  POINTER_ENTER: 'pointerenter',
  POINTER_LEAVE: 'pointerleave',
  DRAG: 'drag',
  DRAG_START: 'dragstart',
  DRAG_END: 'dragend',
  DOUBLE_CLICK: 'doubleclick',
  RIGHT_CLICK: 'contextmenu'
} as const);
export const TOOL = ({
  POINTER: 'POINTER',
  RECTANGLE: 'RECTANGLE',
  ELLIPSE: 'ELLIPSE',
  POLYGON: 'POLYGON',
  KEY_POINT: 'KEY_POINT',
  POLYLINE: 'POLYLINE',
  CUBOID: 'CUBOID',
  SKELETON: 'SKELETON',
  // TODO: Figure out to how to handle other segmentation tools
  SEGMENTATION: 'SEGMENTATION',
  GRID: 'GRID',
  SNAP: 'SNAP'
} as const);
export const CUSTOM_TOOLS = ({
  FRAME_CLASSIFICATION: 'FRAME_CLASSIFICATION'
} as const);
export const SEGMENTATION_TOOLS = ({
  BRUSH: 'BRUSH',
  SEGMENTATION_POLYGON: 'SEGMENTATION_POLYGON',
  SUPERPIXEL: 'SUPERPIXEL',
  ERASER: 'ERASER',
  SEGMENT_SIZE: 'SEGMENT_SIZE',
  SAM_CLICK: 'SAM_CLICK',
  SAM_BOUNDING_BOX: 'SAM_BOUNDING_BOX',
  AUTO_DETECT: 'AUTO_DETECT'
} as const);
export const POLYGON_TOOLS = ({
  POLYGON: 'POLYGON',
  SAM_CLICK: 'POLYGON_SAM_CLICK',
  SAM_BOUNDING_BOX: 'POLYGON_SAM_BOUNDING_BOX',
  AUTO_DETECT: 'POLYGON_AUTO_DETECT'
} as const);
export const SKELETON_TOOLS = ({
  FREE_HAND_SKELETON: 'FREE_HAND_SKELETON',
  HUMAN_SKELETON_TOOL: 'HUMAN_SKELETON_TOOL'
} as const);
export const BOUNDING_BOX_TOOLS = ({
  BOUNDING_BOX: 'BOUNDING_BOX',
  YOLO_BOUNDING_BOX: 'YOLO_BOUNDING_BOX'
} as const);
export const SAM_CLICK = ({
  INCLUDE: 'INCLUDE',
  EXCLUDE: 'EXCLUDE'
} as const);
export const KEYBOARD_SHORTCUTS = Object.freeze({
  BACKSPACE: {
    key: 'Backspace',
    shortcut: 'Del',
    description: 'To delete the selected annotation'
  },
  ESCAPE: {
    key: 'Escape',
    shortcut: 'Esc',
    description: 'To escape the currently drawing annotation'
  },
  CONTROL: {
    key: 'Control',
    shortcut: 'Ctrl',
    description: 'Longpress control key to view annotation tool shortcuts'
  },
  SLASH: {
    key: '/',
    shortcut: 'Ctrl+/',
    description: 'Opens a modal that shows all keyboard shortcut'
  },
  MULTI_SELECT: {
    key: 'Alt+Click',
    shortcut: 'Alt+Click',
    description: 'Allows selecting multiple annotations'
  },
  MOVE: {
    key: 'Arrow Keys',
    shortcut: 'Arrow Keys',
    description: 'Move the selected bounding box'
  }
});
export const IMAGE_DRAG_SHORTCUTS = Object.freeze({
  CONTROL: {
    key: 'Option/Alt',
    shortcut: 'Option/Alt+Drag',
    description: 'Longpress option/alt key to drag the image while drawing labels'
  }
});
export const SIDE_PANEL_SHORCUTS = Object.freeze({
  SELECT_EDIT_TAB: {
    key: 'Control+e',
    shortcut: 'Control+e',
    description: 'Allows selecting the edit tab in side panel'
  },
  SELECT_REVIEW_TAB: {
    key: 'Control+r',
    shortcut: 'Control+r',
    description: 'Allows selecting the review tab in side panel'
  }
});
export const REVIEW_MODE_SHORTCUTS = Object.freeze({
  NEXT_PAGE: {
    key: 'Control+n',
    shortcut: 'Control+n',
    description: 'Allows moving to the next page in review mode'
  },
  PREVIOUS_PAGE: {
    key: 'Control+p',
    shortcut: 'Control+p',
    description: 'Allows moving to the previous page in review mode'
  },
  SAVE: {
    key: 'Control+s',
    shortcut: 'Control+s',
    description: 'Allows saving an annotation after review'
  },
  CANCEL: {
    key: 'Control+c',
    shortcut: 'Control+c',
    description: 'Allows cancelling changes made to an annotation in review mode'
  },
  TOGGLE_SHOW_HIDE_ALL_ANNOTATIONS: {
    key: 'd',
    shortcut: 'd',
    description: 'Allows to toggle between show and hide all filtered annotations'
  },
  TOGGLE_LIST_SINGLE_VIEW: {
    key: 's',
    shortcut: 's',
    description: 'Allows to toggle between list and single view'
  },
  FOCUS_ANNOTATION_LABEL_SELECTOR: {
    key: 'Control+f',
    shortcut: 'Control+f',
    description: 'Allows to focus the annotation label selector'
  },
  DELETE_ANNOTATION: {
    key: 'Control+d',
    shortcut: 'Control+d',
    description: 'Allows to delete the annotation in review mode'
  }
});
export const GRID_MODE = Object.freeze({
  BLACK: 'BLACK',
  GRAY: 'GRAY',
  NONE: 'NONE'
});
export const GRID_MODE_ORDER = [GRID_MODE.NONE, GRID_MODE.BLACK, GRID_MODE.GRAY];
export const VERSION = '1.0';
export const CANVAS_OPERATION = Object.freeze({
  SOURCE_OVER: 'source-over',
  DESTINATION_OUT: 'destination-out',
  XOR: 'xor'
});
export const DEFAULT_BRUSH_TOOL_SETTINGS = {
  selectedTool: SEGMENTATION_TOOLS.SAM_CLICK,
  eraserSelected: false,
  inclusionExclusion: SAM_CLICK.INCLUDE
};
export const LABELLING = Object.freeze({
  IMAGE_LABELLING: 'image-labelling',
  VIDEO_LABELLING: 'video-labelling'
});
export const VIDEO_PLAYER_CONTROL_TYPES = Object.freeze({
  PLAY: 'PLAY',
  PAUSE: 'PAUSE',
  FORWARD: 'FORWARD',
  BACKWARD: 'BACKWARD',
  SKIP_TO_FIRST: 'SKIP_TO_FIRST',
  SKIP_TO_LAST: 'SKIP_TO_LAST',
  JUMP_FORWARD: 'JUMP_FORWARD',
  JUMP_BACKWARD: 'JUMP_BACKWARD'
});
export const SHORTCUT_REGISTRATION_ERROR: {
  MESSAGE: 'Shortcut key is already registered.';
  TYPE: 'SHORTCUT_KEY_ALREADY_EXISTS';
} = Object.freeze({
  TYPE: 'SHORTCUT_KEY_ALREADY_EXISTS',
  MESSAGE: 'Shortcut key is already registered.'
});
export const ANNOTATION_ML_STATE = Object.freeze({
  //Provided from ML model but not edited
  AUTOMATED: 'AUTOMATED',
  // After any editing done from AUTOMATED state
  ASSISTED: 'ASSISTED'
});
export const SAM_ANNOTATION_ACTIONS = ({
  BOUNDING_BOX: 'BOUNDING_BOX',
  POINT: 'POINT',
  POLYGON: 'POLYGON',
  AUTO_DETECT: 'AUTO_DETECT',
  BOUNDING_BOX_POLYGON: 'BOUNDING_BOX_POLYGON',
  AUTO_DETECT_POLYGON: 'AUTO_DETECT_POLYGON'
} as const);
export const SAM_SEGMENT_ANNOTATION = [SAM_ANNOTATION_ACTIONS.BOUNDING_BOX, SAM_ANNOTATION_ACTIONS.POINT, SAM_ANNOTATION_ACTIONS.AUTO_DETECT];
export const ANNOTATION_SHAPE_STYLING_TYPE = Object.freeze({
  DASHED_BORDER: 'DASHED_BORDER'
});
export const ANNOTATION_STYLING_DETAILS = {
  [ANNOTATION_SHAPE_STYLING_TYPE.DASHED_BORDER]: {
    strokeDasharray: 5
  }
};
export const SUPERPIXEL_SEGMENTS_STYLING_DETAILS = {
  OPACITY: 0.7,
  STROKE_WIDTH: 0.25,
  BORDER_COLOR: 'rgba(255, 255, 255, 0.6)',
  HOVER_COLOR: 'rgba(211, 211, 211, 0.5)',
  SELECT_COLOR: 'rgba(245, 245, 245, 0.5)'
};
export const LAYER_ZINDEX = 1;
export const SUPERPIXEL_CREATE_DELETE_ANNOTATION_MSG = 'Annotate selected regions by simply double-clicking anywhere on the document. Right-click to delete the selected regions.';
export const CREATE_DELETE_SUPERPIXEL_ANNOTATION_NOTIFICATION = 'Create/Delete Superpixel Annotation Notification';
export const SEGMENTATION_POLYGON_STROKE_SIZE = 10;
export const MAX_BRUSH_STROKE_SIZE = 100;
export const MIN_BRUSH_STROKE_SIZE = 1;
export const SLIDER_ICONS_SIZE = 10;
export const BACKGROUND_COLOR = '#F3F3F3';
export const FEATURE_FLAGS = ({
  RELATIONSHIP_MODE_FEATURE_NAME: 'ANNOTATION_RELATIONSHIP',
  MULTI_SELECT_FEATURE_NAME: 'MULTI_SELECT',
  REVIEW_MODE_ENABLE_CONTINUOUS_PLAYBACK: 'REVIEW_MODE_ENABLE_CONTINUOUS_PLAYBACK',
  REVIEW_MODE_ENABLE_BATCH_MODE: 'REVIEW_MODE_ENABLE_BATCH_MODE',
  ENABLE_MODERNIZED_ENTITY_TAGGING_PANEL: 'ENABLE_MODERNIZED_ENTITY_TAGGING_PANEL',
  VIDEO_GRID: 'VIDEO_GRID',
  SEGMENTATION_USER_EVENTS: 'SEGMENTATION_USER_EVENTS',
  M3_SIDE_PANEL: 'M3_SIDE_PANEL',
  ENABLE_REMAINING_ITEMS_SIDE_PANEL_M3: 'ENABLE_REMAINING_ITEMS_SIDE_PANEL_M3',
  ENABLE_TRACKING_DETAILS_VALIDATION: 'ENABLE_TRACKING_DETAILS_VALIDATION',
  ENABLE_KEYFRAME_STEPPER_TRACKING: 'ENABLE_KEYFRAME_STEPPER_TRACKING',
  ENABLE_ACCURATE_FRAME_RENDERING: 'ENABLE_ACCURATE_FRAME_RENDERING',
  ENABLE_IMAGE_CANT_BE_LABELLED: 'ENABLE_IMAGE_CANT_BE_LABELLED',
  LABELING_STUDIO_V2: 'ENABLE_VIDEO_LABELING_STUDIO_V2',
  /** Account feature key (labeling-web `ACCOUNT_FEATURES`); used for image quadrant remaining-items validation. */
  IMAGE_QUADRANT_VALIDATION_REMAINING_ITEMS: 'image_quadrant_validation_remaining_items',
  MAGNETIC_SNAPPING_IN_TRANSCRIPTION: 'magnetic_snapping_in_transcription',
  ENABLE_MULTI_CHANNEL_SUPPORT: 'enable_multichannel_support',
  TRANSCRIPTION_AUTO_SAVE: 'transcription_auto_save',
  TRANSCRIPTION_FORMAT_VALIDATIONS: 'transcription_format_validations',
  TRANSCRIPTION_DISABLED_DEFAULT_OVERLAP_TAG: 'disabled_default_overlap_tag',
  ALLOW_MERGING_DISCONTINUOUS_SLOTS: 'allow_merging_discontinuous_slots',
  WAVEFORM_TINT: 'waveform_tint',
  WORD_MODE: 'word_mode',
  // FIXME:  (LINEAR-LTP-3251) This is a hack added for a very Zoom specific use case.
  // refer https://uber.enterprise.slack.com/archives/C0AMU5PPK9C
  ZOOM_MULTI_TRANSCRIPT: 'ZOOM_MULTI_TRANSCRIPT',
  // added this for backward compatibility and to promote saving feature flags in common package
  ...COMMON_FEATURE_FLAGS
} as const);
export const ACCURATE_FRAME_RENDERING_LOADING = ('loading' as const);
export const POINT_BOUNDING_BOX_DEFAULT_DIMENSION = 120;
export const EMPTY_ARRAY = [];
export const EMPTY_OBJECT = {};
export const SEGMENTATION_ALPHA = 128;
export const ANNOTATION_OUTLINE_ALPHA = 192;
export const ANNOTATION_OUTLINE_SHADOW_ALPHA = 0.5;
export const FRAME_ASCENDING_ORDER_LABEL = 'By Frame in Ascending order';
export const FRAME_DESCENDING_ORDER_LABEL = 'By Frame in Descending order';
export const LABELLING_REVIEW_PANEL_SETTINGS = {
  viewMode: VIEW_MODE.SINGLE,
  enableAcceptAll: false,
  enableContinuousPlayback: false,
  showAllAnnotations: true
};
export const MAX_FRAMES_PER_TRACKING_CALL = 30;
export const TRACK_NEXT_FRAMES_COUNT = 30;
export const BBOX_TRACKING_NUM_FRAMES_TO_TRACK = 100;
export const TRACK_OVERRIDE_MODAL = ({
  TITLE: 'Override Existing Annotations?',
  WARNING_MESSAGE: (startFrame: number, endFrame: number, labelTitle: string) => `Any manual changes made to frames ${startFrame}-${endFrame} for label "${labelTitle}" will be overridden.`,
  CONFIRMATION_MESSAGE: 'Do you want to override the existing annotations and track the new ones?',
  CANCEL_BUTTON: 'Cancel',
  CONFIRM_BUTTON: 'Override & Track',
  ICON: {
    SIZE: 20
  }
} as const);
export const BORDER_WIDTH = ({
  MIN_WIDTH: 2,
  MAX_WIDTH: 6
} as const);
export const SELECTION_ALPHA = ({
  MIN_ALPHA: 160,
  BASE_ALPHA: 224
} as const);
export const getAnnotationOutlineAlpha = () => {
  const scale = Math.sqrt(window.devicePixelRatio || 1);
  return Math.min(255, Math.max(SELECTION_ALPHA.MIN_ALPHA, Math.round(SELECTION_ALPHA.BASE_ALPHA * scale)));
};
export const getAnnotationOutlineWidth = () => {
  const scale = window.devicePixelRatio || 1;
  const rawWidth = BORDER_WIDTH.MAX_WIDTH / scale;
  return Math.max(BORDER_WIDTH.MIN_WIDTH, Math.min(BORDER_WIDTH.MAX_WIDTH, Math.round(rawWidth)));
};
export const MAX_OUTLINE_PATH_CACHE_SIZE = 50;
export const MAX_CENTROID_CACHE_SIZE = 100;
export const CENTROID_EARLY_EXIT_SAMPLE_RATE = 64; // Coarse sampling for quick empty check
export const CENTROID_DENSITY_SAMPLE_RATE = 16; // Sample every 16th pixel for density estimation
export const CENTROID_HIGH_DENSITY_SAMPLE_RATE = 2; // Use every 2nd pixel for dense annotations
export const CENTROID_LOW_DENSITY_SAMPLE_RATE = 4; // Use every 4th pixel for sparse annotations
export const CENTROID_DENSITY_THRESHOLD = 0.1; // Density threshold to switch between sample rates

export const HASH_MULTIPLIER = 31; // Prime number used in polynomial rolling hash algorithm
export const BITMAP_HASH_SAMPLE_RATE = 64; // Sample every 64th pixel for content hash generation (reduced from 256 for better sensitivity to SAM tracking changes)

export const CENTROID_SMALL_IMAGE_THRESHOLD = 50000; // Use full scan for images smaller than 50k pixels
export const CENTROID_SMALL_IMAGE_EARLY_EXIT_RATE = 16; // Finer early exit for small images

export const CONTOUR_DIRECTIONS = ([{
  dx: 1,
  dy: 0
}, {
  dx: 1,
  dy: 1
}, {
  dx: 0,
  dy: 1
}, {
  dx: -1,
  dy: 1
}, {
  dx: -1,
  dy: 0
}, {
  dx: -1,
  dy: -1
}, {
  dx: 0,
  dy: -1
}, {
  dx: 1,
  dy: -1
}] as const);
export const DYNAMIC_ATTRIBUTE_TAG = 'dynamic_field';
export const DYNAMIC_ATTRIBUTE_LS_TAG = 'system_ls_dynamic';
export const DEFAULT_BRUSH_SIZE = 20;
export const MAIN_VIDEO_ID = 'main-video';
export const THUMBNAIL_VIDEO_ID = 'thumbnail-preview-video';

//This is added to keep track of how and to what degree layer zIndex are being overridden
export const Z_INDEX_LAYERS = {
  ONE: 1,
  TWO: 2,
  THREE: 3,
  FOUR: 4,
  FIVE: 5
};